import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { TooltipOptions } from 'weavix-shared/models/tooltip.model';

export enum ToggleChipType {
    Warning = 'warning',
    Watch = 'watch',
}

export interface ToggleChip {
    id: string;
    name: string;
    type?: ToggleChipType; // warning => red, watch => yellow; unselected chips are always gray
    hidden?: boolean;
}

@Component({
    selector: 'app-toggle-chip-list',
    templateUrl: './toggle-chip-list.component.html',
    styleUrls: ['./toggle-chip-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        TooltipComponent,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ToggleChipListComponent),
            multi: true,
        },
    ],
})
export class ToggleChipListComponent implements ControlValueAccessor {
    @Input({ required: true }) dataSource: ToggleChip[] = [];
    @Input() label = '';
    @Input() showLabel = true;
    @Input() doTranslate = true;
    @Input() translateItems = false;
    @Input() required = false;
    @Input() tooltip: TooltipOptions;

    @Output() valueChanged = new EventEmitter<string[]>();

    selectedIds: string[] = [];
    isDisabled = false;

    private onChange: (value: string[]) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private readonly cdr: ChangeDetectorRef) {}

    writeValue(value: string[]): void {
        this.selectedIds = Array.isArray(value) ? [...value] : [];
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: string[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        this.cdr.markForCheck();
    }

    isSelected(id: string): boolean {
        return this.selectedIds.includes(id);
    }

    toggle(chip: ToggleChip): void {
        if (this.isDisabled) return;
        this.selectedIds = this.isSelected(chip.id)
            ? this.selectedIds.filter(id => id !== chip.id)
            : [...this.selectedIds, chip.id];
        this.onChange(this.selectedIds);
        this.onTouched();
        this.valueChanged.emit(this.selectedIds);
        this.cdr.markForCheck();
    }
}
